import MobileDetect from "mobile-detect"
import { action, computed, makeObservable, observable } from "mobx"
import { isMobile } from "@lib/device"
import { BreakPoints } from "styles/styles"
import { isServer } from "@lib/isomorphic"
import Parser from "ua-parser-js"

import { BaseModel } from "./base.model"

export class ResponsiveModel extends BaseModel {
  // @ts-ignore
  constructor(params) {
    super(params)

    makeObservable(this)
  }

  @observable touchDevice = false
  @observable width = 0
  @observable height = 0
  @observable isMobile: boolean = true
  @observable isAndroid: boolean = false
  @observable isHighResolution: boolean = false

  @observable resizeTimeoutId: number | null = null
  @observable orientationChangeTimeoutId: number | null = null

  @observable userAgent: string = ""
  @observable deviceType?: "mobile" | "tablet" | "desktop"
  @observable browserName?: string
  @observable browserVersion?: string
  @observable deviceOS?: string
  @observable deviceOSVersion?: string

  @computed get user_agent() {
    return isServer()
      ? this.userAgent
      : window.navigator.userAgent || window.navigator.vendor
  }

  setUserAgent = (userAgent?: string) => {
    const parsedUA = userAgent ? Parser(userAgent) : new Parser().getResult()
    const deviceType = ["mobile", "tablet"].includes(parsedUA.device.type ?? "")
      ? parsedUA.device.type
      : "desktop"
    this.applyData({
      userAgent: parsedUA.ua,
      browserName: parsedUA.browser.name,
      browserVersion: parsedUA.browser.version,
      deviceOS: parsedUA.os.name,
      deviceOSVersion: parsedUA.os.version,
      isMobile: deviceType === "mobile",
      deviceType,
    })
  }

  onWindowResize = () => {
    this.setWidth()
    this.setHeight()
    const mobile = isMobile(
      window.navigator.userAgent || window.navigator.vendor,
    )
    if (mobile !== this.isMobile) this.setIsMobile(mobile)
  }

  calculateIsHighResolution = () => {
    return window.devicePixelRatio > 1
  }

  postInit = () => {
    this.setUserAgent()
    this.applyData({
      touchDevice: this.calculateIsTouch(),
      isAndroid: this.is_android,
      isHighResolution: this.calculateIsHighResolution(),
    })
    this.setWidth()
    this.setHeight()

    window.addEventListener("resize", () => {
      if (this.resizeTimeoutId) {
        window.clearTimeout(this.resizeTimeoutId)
      }
      this.applyData({
        resizeTimeoutId: window.setTimeout(this.onWindowResize, 200),
      })
    })
    window.addEventListener("orientationchange", () => {
      if (this.orientationChangeTimeoutId) {
        window.clearTimeout(this.orientationChangeTimeoutId)
      }
      this.applyData({
        orientationChangeTimeoutId: window.setTimeout(this.onWindowResize, 200),
      })
    })
  }

  setWidth = action(() => {
    if (this.width === window.innerWidth) return
    this.width = window.innerWidth
  })

  setHeight = action(() => {
    if (this.height === window.innerHeight) return
    this.height = window.innerHeight
  })

  setIsMobile = (isMobile: boolean) => {
    this.applyData({
      isMobile,
    })
  }

  calculateIsTouch = () => {
    const userAgent = this.user_agent
    const detect = new MobileDetect(userAgent)
    let touchDevice = false
    try {
      document.createEvent("TouchEvent")
      touchDevice = true
    } catch (e) {}

    return !!(
      detect.mobile() ||
      detect.phone() ||
      detect.tablet() ||
      touchDevice
    )
  }

  @computed get is_ios() {
    const userAgent = this.user_agent
    const md = new MobileDetect(userAgent)
    return md.os() === "iOS"
  }

  @computed get is_android() {
    const userAgent = this.user_agent
    const md = new MobileDetect(userAgent)
    return md.os() === "AndroidOS"
  }

  @computed get is_safari() {
    const userAgent = this.user_agent
    return userAgent.includes("Safari") && !userAgent.includes("Chrome")
  }

  @computed get isWindowsOS() {
    const userAgent = this.user_agent
    return userAgent.includes("Windows")
  }

  @computed get laptop() {
    return (
      this.width >= BreakPoints.laptop && this.width < BreakPoints.large_desktop
    )
  }

  @computed get large_desktop() {
    return this.width >= BreakPoints.large_desktop
  }

  @computed get desktop() {
    return this.width >= BreakPoints.laptop
  }

  @computed get small_desktop() {
    return this.width < BreakPoints.laptop_small
  }

  @computed get touch() {
    return this.width < BreakPoints.laptop_small || this.touchDevice
  }

  @computed get tablet() {
    return this.width >= BreakPoints.tablet && this.width < BreakPoints.laptop
  }

  @computed get mobile() {
    return this.width < BreakPoints.tablet
  }

  @computed get xs_mobile() {
    return this.width < BreakPoints.xs_mobile
  }

  @computed get is_large_size() {
    return this.width >= BreakPoints.large
  }

  @computed get is_medium_size() {
    return this.width > BreakPoints.small && this.width <= BreakPoints.medium
  }

  @computed get is_small_size() {
    return this.width <= BreakPoints.small
  }

  @computed get screenSize() {
    return this.is_large_size
      ? "large"
      : this.is_medium_size
      ? "medium"
      : "small"
  }

  // TODO: Remove from code everythere if OK on production
  @computed get isWebPSupported() {
    return true
  }

  hydrate = (data: unknown) => {
    // @ts-ignore
    this.applyData({ isMobile: data?.isMobile })
    // @ts-ignore
    this.applyData({ touchDevice: data?.touchDevice })
    // @ts-ignore
    this.applyData({ width: data?.width })
    // @ts-ignore
    this.applyData({ isAndroid: data?.isAndroid })
    // @ts-ignore
    this.applyData({ userAgent: data?.userAgent })
    // @ts-ignore
    this.applyData({ deviceType: data?.deviceType })
    // @ts-ignore
    this.applyData({ browserName: data?.browserName })
    // @ts-ignore
    this.applyData({ deviceOS: data?.deviceOS })
    // @ts-ignore
    this.applyData({ deviceOSVersion: data?.deviceOSVersion })
    // @ts-ignore
    this.applyData({ browserVersion: data?.browserVersion })

    if (!isServer()) {
      this.setWidth()
      this.setHeight()
    }
  }

  serialize = () => {
    return {
      isMobile: this.isMobile,
      touchDevice: this.touchDevice,
      width: this.width,
      isAndroid: this.isAndroid,
      userAgent: this.userAgent,
      deviceType: this.deviceType,
      browserName: this.browserName,
      deviceOS: this.deviceOS,
      deviceOSVersion: this.deviceOSVersion,
      browserVersion: this.browserVersion,
    }
  }
}
